/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 水印应用相关 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-27 15:49:51
 */

import { useState, useCallback } from 'react'
import { message } from 'antd'
import {
  WatermarkExtractionStatus,
  WatermarkItem,
  PhotoItem
} from '../context/WatermarkBatchContext'
import { useAITaskEvent, fetchOutputFromUrl } from './useAITaskEvent'
import { useUserId } from '../../../../app/hooks'
import { useWatermarkBatchEditorTrack } from './useWatermarkBatchEditorTrack'

/**
 * 水印应用输出结果中的 items 项
 */
export interface WatermarkApplyOutputItem {
  item_id: number
  title: string
  content: string
  switch: boolean
  item_status: number
  edit_type?: number // 编辑类型
}

/**
 * 水印应用状态枚举
 */
export enum WatermarkApplyStatus {
  FAILED = 0, // 编辑失败
  EDITED = 1, // 已编辑
  NO_CHANGES = 2, // 无修改项
  TIMEOUT = 3 // 编辑超时
}

/**
 * 水印应用输出结果项
 */
export interface WatermarkApplyOutputResult {
  src: string
  res: string
  watermark_id: string
  success: WatermarkApplyStatus // 状态码：0-编辑失败，1-已编辑，2-无修改项，3-编辑超时
  reason: string
  show_reason: string
  items: WatermarkApplyOutputItem[]
}

/**
 * 将 API 返回的 WatermarkApplyOutputItem 转换为 WatermarkItem
 * @param item API 返回的项
 * @param originalItem 原始的 WatermarkItem（用于保留 contentEdit 等信息）
 */
const convertOutputItemToWatermarkItem = (
  item: WatermarkApplyOutputItem,
  originalItem?: WatermarkItem
): WatermarkItem => {
  return {
    item_id: String(item.item_id), // 将 number 转换为 string
    title: item.title,
    content: item.content,
    switch: item.switch,
    item_status: item.item_status,
    edit_type: item.edit_type, // 保留 edit_type 字段
    // 保留原有的 contentEdit 和 options，这些信息应用接口不返回
    ...(originalItem?.contentEdit && { contentEdit: originalItem.contentEdit }),
    ...(originalItem?.options && { options: originalItem.options }),
    // 保留其他可能存在的字段
    ...(originalItem?.id && { id: originalItem.id }),
    ...(originalItem?.watermarkID && { watermarkID: originalItem.watermarkID })
  }
}

interface UseWatermarkApplyParams {
  photoList: PhotoItem[]
  currentIndex: number
  currentPhoto: PhotoItem | null
  updatePhotosStatus: (
    photos: PhotoItem[],
    status: WatermarkExtractionStatus
  ) => void
}

interface ResultModalState {
  visible: boolean
  successCount: number
  successPhotos: PhotoItem[] // 成功照片列表
  noChangesCount: number // 无修改项数量
  noChangesPhotos: PhotoItem[] // 无修改项照片列表
  timeoutCount: number // 超时数量
  timeoutPhotos: PhotoItem[] // 超时照片列表
  failedCount: number
  failedPhotos: PhotoItem[]
  failureReason: string
}

export const useWatermarkApply = ({
  photoList,
  currentIndex,
  currentPhoto,
  updatePhotosStatus
}: UseWatermarkApplyParams) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [processingPhotoList, setProcessingPhotoList] = useState<PhotoItem[]>(
    []
  )
  const [processingPhotoCount, setProcessingPhotoCount] = useState<number>(0)
  const [resultModal, setResultModal] = useState<ResultModalState>({
    visible: false,
    successCount: 0,
    successPhotos: [],
    noChangesCount: 0,
    noChangesPhotos: [],
    timeoutCount: 0,
    timeoutPhotos: [],
    failedCount: 0,
    failedPhotos: [],
    failureReason: ''
  })

  const userId = useUserId()
  const { createAITask, pollAITaskResult } = useAITaskEvent()
  const { trackWatermarkBatchEditor } = useWatermarkBatchEditorTrack()

  /**
   * 根据应用类型构建请求参数
   */
  const buildApplyParams = useCallback(
    (
      applyType: 'current' | 'all' | 'selected',
      selectedPhotos: PhotoItem[] = []
    ): {
      urlList: string[]
      photoCount: number
      processingPhotos: PhotoItem[]
      successMessage: string
    } | null => {
      if (applyType === 'selected') {
        const validPhotos = selectedPhotos.filter(
          (photo) => photo.watermarkStatus !== WatermarkExtractionStatus.FAILED
        )

        const urlList = validPhotos
          .map((photo) => photo.aiURL || photo.smallURL || '')
          .filter((url) => url !== '')

        // photoCount = select_url (1张) + urls (urlList.length)
        const photoCount = currentPhoto ? urlList.length + 1 : urlList.length

        return {
          urlList,
          photoCount,
          processingPhotos: validPhotos,
          successMessage: `水印已应用到选中的 ${urlList.length} 张照片！`
        }
      }

      if (applyType === 'all') {
        const validPhotos = photoList.filter(
          (photo) => photo.watermarkStatus !== WatermarkExtractionStatus.FAILED
        )

        const urlList = photoList
          .filter((_, index) => index !== currentIndex)
          .filter(
            (photo) =>
              photo.watermarkStatus !== WatermarkExtractionStatus.FAILED
          )
          .map((photo) => photo.aiURL || photo.smallURL || '')
          .filter((url) => url !== '')

        // photoCount = select_url (1张) + urls (urlList.length)
        const photoCount = currentPhoto ? urlList.length + 1 : urlList.length

        return {
          urlList,
          photoCount,
          processingPhotos: validPhotos,
          successMessage: '水印已应用到所有图片！'
        }
      }

      // applyType === 'current'
      return {
        urlList: [],
        photoCount: 1,
        processingPhotos: currentPhoto ? [currentPhoto] : [],
        successMessage: '水印已应用到当前照片！'
      }
    },
    [photoList, currentIndex, currentPhoto]
  )

  /**
   * 构建 src_list 和 edit_list
   */
  const buildWatermarkLists = useCallback(
    (
      editedItems: WatermarkItem[],
      currentPhoto: PhotoItem | null
    ): {
      srcList: any[]
      editList: any[]
    } => {
      const isCustomItem = (item: WatermarkItem) =>
        (item as any).userCustom === true

      // 找出所有新增的自定义项
      const customItems = editedItems.filter(isCustomItem)

      // 为自定义项创建空的 item 对象（用于 src_list）
      const emptyCustomItems = customItems.map((item) => ({
        item_id: -1,
        title: '',
        content: '',
        switch: false,
        item_status: 1,
        edit_type: 3
      }))

      // 构建 src_list，包含原始数据 + 自定义项的空对象
      // 只传入 edit_type 为 2、3、5 的项，过滤掉 edit_type 为 0、1、4 的项
      const srcList = [
        ...(currentPhoto?.watermarkData ?? [])
          .filter((item) => {
            const editType = item.edit_type
            return editType === 2 || editType === 3 || editType === 5
          })
          .map((item) => ({
            ...item,
            item_status: item.item_status !== undefined ? item.item_status : 0
          })),
        ...emptyCustomItems
      ]

      // 构建 edit_list
      const editList = editedItems
        .filter((item) => {
          const editType = item.edit_type
          if (isCustomItem(item)) {
            return true
          }
          return editType === 2 || editType === 3 || editType === 5
        })
        .map((item) => {
          if (isCustomItem(item)) {
            return {
              item_id: -1,
              title: item.title || '',
              content: item.content || '',
              switch: item.switch,
              item_status: 1,
              edit_type: 3
            }
          }
          return {
            item_id: Number(item.item_id),
            title: item.title || '',
            content: item.content || '',
            switch: item.switch,
            item_status: item.item_status || 0,
            edit_type: item.edit_type || 0
          }
        })

      return { srcList, editList }
    },
    []
  )

  /**
   * 标准化 URL（去除查询参数）
   */
  const normalizeUrl = (url: string): string => {
    if (!url) return ''
    try {
      const urlObj = new URL(url)
      return `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`
    } catch {
      return url.split('?')[0]
    }
  }

  /**
   * 在照片列表中查找匹配的照片
   */
  const findMatchedPhoto = useCallback((src: string, photoList: PhotoItem[]):
    | PhotoItem
    | undefined => {
    const normalizedSrc = normalizeUrl(src)
    return photoList.find((photo) => {
      const normalizedSmallURL = normalizeUrl(photo.smallURL)
      const normalizedAiURL = normalizeUrl(photo.aiURL)
      const normalizedUrl = normalizeUrl(photo.url)
      return (
        normalizedSmallURL === normalizedSrc ||
        normalizedAiURL === normalizedSrc ||
        normalizedUrl === normalizedSrc ||
        photo.smallURL === src ||
        photo.aiURL === src ||
        photo.url === src
      )
    })
  }, [])

  /**
   * 合并 watermarkData，保留原有的 contentEdit 等信息
   */
  const mergeWatermarkData = (
    outputItems: WatermarkApplyOutputItem[],
    originalWatermarkData: WatermarkItem[]
  ): WatermarkItem[] => {
    const originalItemMap = new Map(
      originalWatermarkData.map((item) => [String(item.item_id), item])
    )

    return outputItems.map((outputItem) => {
      const originalItem = originalItemMap.get(String(outputItem.item_id))
      return convertOutputItemToWatermarkItem(outputItem, originalItem)
    })
  }

  /**
   * 处理单个输出项，更新照片状态
   */
  const processOutputItem = useCallback(
    (
      outputItem: WatermarkApplyOutputResult,
      taskId: string,
      photoList: PhotoItem[]
    ): {
      editedPhotos: PhotoItem[]
      noChangesPhotos: PhotoItem[]
      timeoutPhotos: PhotoItem[]
      failedPhotos: PhotoItem[]
      failureReasons: string[]
    } => {
      const editedPhotos: PhotoItem[] = []
      const noChangesPhotos: PhotoItem[] = []
      const timeoutPhotos: PhotoItem[] = []
      const failedPhotos: PhotoItem[] = []
      const failureReasons: string[] = []

      const matchedPhoto = findMatchedPhoto(outputItem.src, photoList)

      if (!matchedPhoto) {
        return {
          editedPhotos,
          noChangesPhotos,
          timeoutPhotos,
          failedPhotos,
          failureReasons
        }
      }

      const applyStatus = Number(outputItem.success)
      const hasResult = outputItem.res
      const originalWatermarkData = matchedPhoto.watermarkData || []

      if (applyStatus === WatermarkApplyStatus.EDITED && hasResult) {
        // 编辑成功
        const watermarkData = mergeWatermarkData(
          outputItem.items || [],
          originalWatermarkData
        )
        const updatedPhoto: PhotoItem = {
          ...matchedPhoto,
          watermarkStatus: WatermarkExtractionStatus.EDIT_COMPLETED,
          watermarkData,
          aiURL: outputItem.res,
          ...(outputItem.watermark_id && {
            watermarkID: outputItem.watermark_id
          })
        }
        editedPhotos.push(updatedPhoto)
      } else if (applyStatus === WatermarkApplyStatus.NO_CHANGES) {
        // 无修改项
        const watermarkData = mergeWatermarkData(
          outputItem.items || [],
          originalWatermarkData
        )
        const updatedPhoto: PhotoItem = {
          ...matchedPhoto,
          watermarkStatus: WatermarkExtractionStatus.EDIT_NO_CHANGES,
          watermarkData,
          errorMsg: outputItem.show_reason || outputItem.reason || '无修改项',
          reason: outputItem.reason,
          ...(outputItem.watermark_id && {
            watermarkID: outputItem.watermark_id
          })
        }
        noChangesPhotos.push(updatedPhoto)
      } else if (applyStatus === WatermarkApplyStatus.TIMEOUT) {
        // 编辑超时
        const updatedPhoto: PhotoItem = {
          ...matchedPhoto,
          watermarkStatus: WatermarkExtractionStatus.EDIT_TIMEOUT,
          errorMsg: outputItem.show_reason || outputItem.reason || '编辑超时',
          reason: outputItem.reason,
          ...(outputItem.watermark_id && {
            watermarkID: outputItem.watermark_id
          })
        }
        timeoutPhotos.push(updatedPhoto)
      } else {
        // 编辑失败
        const errorMsg =
          outputItem.show_reason || outputItem.reason || '处理失败'

        // 失败时保持原来的 watermarkData，不替换（因为失败时 items 是空数组）
        const updatedPhoto: PhotoItem = {
          ...matchedPhoto,
          watermarkStatus: WatermarkExtractionStatus.EDIT_FAILED,
          watermarkData: originalWatermarkData,
          errorMsg,
          reason: outputItem.reason,
          taskID: taskId || matchedPhoto.taskID,
          ...(outputItem.watermark_id && {
            watermarkID: outputItem.watermark_id
          })
        }
        failedPhotos.push(updatedPhoto)

        if (errorMsg && !failureReasons.includes(errorMsg)) {
          failureReasons.push(errorMsg)
        }
      }

      return {
        editedPhotos,
        noChangesPhotos,
        timeoutPhotos,
        failedPhotos,
        failureReasons
      }
    },
    [findMatchedPhoto]
  )

  /**
   * 更新照片状态
   */
  const updatePhotosStatusByCategory = useCallback(
    (
      editedPhotos: PhotoItem[],
      noChangesPhotos: PhotoItem[],
      timeoutPhotos: PhotoItem[],
      failedPhotos: PhotoItem[]
    ) => {
      if (editedPhotos.length > 0) {
        updatePhotosStatus(
          editedPhotos,
          WatermarkExtractionStatus.EDIT_COMPLETED
        )
      }
      if (noChangesPhotos.length > 0) {
        updatePhotosStatus(
          noChangesPhotos,
          WatermarkExtractionStatus.EDIT_NO_CHANGES
        )
      }
      if (timeoutPhotos.length > 0) {
        updatePhotosStatus(
          timeoutPhotos,
          WatermarkExtractionStatus.EDIT_TIMEOUT
        )
      }
      if (failedPhotos.length > 0) {
        updatePhotosStatus(failedPhotos, WatermarkExtractionStatus.EDIT_FAILED)
      }
    },
    [updatePhotosStatus]
  )

  /**
   * 设置结果弹窗
   */
  const setResultModalData = useCallback(
    (
      editedPhotos: PhotoItem[],
      noChangesPhotos: PhotoItem[],
      timeoutPhotos: PhotoItem[],
      failedPhotos: PhotoItem[],
      failureReasons: string[]
    ) => {
      setResultModal({
        visible: true,
        successCount: editedPhotos.length,
        successPhotos: editedPhotos,
        noChangesCount: noChangesPhotos.length,
        noChangesPhotos,
        timeoutCount: timeoutPhotos.length,
        timeoutPhotos,
        failedCount: failedPhotos.length,
        failedPhotos,
        failureReason:
          failureReasons.length > 0
            ? Array.from(new Set(failureReasons)).join('；')
            : failedPhotos.length > 0
            ? '处理失败，请重试'
            : ''
      })

      // 埋点上报：成功数量 = editedPhotos，失败数量 = failedPhotos + timeoutPhotos（超时也算失败）
      trackWatermarkBatchEditor({
        actionType: 'showProcessCompletePop',
        number5: editedPhotos.length,
        number6: failedPhotos.length + timeoutPhotos.length,
        photoNum: photoList.length
      })
    },
    [trackWatermarkBatchEditor, photoList]
  )

  /**
   * 处理失败情况（统一处理）
   */
  const handleFailure = useCallback(
    (photos: PhotoItem[], errorMsg: string, currentPhoto: PhotoItem | null) => {
      const finalPhotos =
        photos.length > 0 ? photos : currentPhoto ? [currentPhoto] : []

      const failedPhotosWithError = finalPhotos.map((photo) => ({
        ...photo,
        watermarkStatus: WatermarkExtractionStatus.EDIT_FAILED,
        errorMsg
      }))

      if (failedPhotosWithError.length > 0) {
        updatePhotosStatus(
          failedPhotosWithError,
          WatermarkExtractionStatus.EDIT_FAILED
        )
      }

      setResultModal({
        visible: true,
        successCount: 0,
        successPhotos: [],
        noChangesCount: 0,
        noChangesPhotos: [],
        timeoutCount: 0,
        timeoutPhotos: [],
        failedCount: finalPhotos.length,
        failedPhotos: failedPhotosWithError,
        failureReason: errorMsg
      })

      trackWatermarkBatchEditor({
        actionType: 'showProcessCompletePop',
        number5: 0,
        number6: finalPhotos.length,
        photoNum: photoList.length
      })
    },
    [updatePhotosStatus, trackWatermarkBatchEditor, photoList]
  )

  /**
   * 从照片列表中按状态分类
   */
  const categorizePhotosByStatus = useCallback((photos: PhotoItem[]) => {
    return {
      editedPhotos: photos.filter(
        (photo) =>
          photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED
      ),
      noChangesPhotos: photos.filter(
        (photo) =>
          photo.watermarkStatus === WatermarkExtractionStatus.EDIT_NO_CHANGES
      ),
      timeoutPhotos: photos.filter(
        (photo) =>
          photo.watermarkStatus === WatermarkExtractionStatus.EDIT_TIMEOUT
      ),
      failedPhotos: photos.filter(
        (photo) =>
          photo.watermarkStatus === WatermarkExtractionStatus.EDIT_FAILED
      )
    }
  }, [])

  /**
   * 获取需要失败处理的照片列表（统一逻辑）
   */
  const getPhotosToFail = useCallback(
    (
      currentProcessingPhotos: PhotoItem[],
      processingPhotoList: PhotoItem[]
    ): PhotoItem[] => {
      return currentProcessingPhotos.length > 0
        ? currentProcessingPhotos
        : processingPhotoList
    },
    []
  )

  /**
   * 处理轮询结果
   */
  const handlePollResult = useCallback(
    async (
      pollResult: any,
      taskId: string,
      currentProcessingPhotos: PhotoItem[],
      processingPhotoList: PhotoItem[],
      successMessage: string
    ) => {
      if (pollResult.data?.taskStatus === 3) {
        // 任务成功
        if (pollResult.data.output) {
          try {
            const isUrl =
              typeof pollResult.data.output === 'string' &&
              (pollResult.data.output.startsWith('http://') ||
                pollResult.data.output.startsWith('https://'))

            let outputData: WatermarkApplyOutputResult[]

            if (isUrl) {
              outputData = await fetchOutputFromUrl<
                WatermarkApplyOutputResult[]
              >(pollResult.data.output)
            } else {
              outputData = JSON.parse(pollResult.data.output)
            }

            if (Array.isArray(outputData) && outputData.length > 0) {
              // 处理输出数据
              // 确定要搜索的照片列表：优先使用 currentProcessingPhotos，否则使用 processingPhotoList
              // 确保包含 currentPhoto，因为 select_url 的结果需要匹配到它
              let photosToSearch =
                currentProcessingPhotos.length > 0
                  ? currentProcessingPhotos
                  : processingPhotoList.length > 0
                  ? processingPhotoList
                  : photoList

              // 如果 photosToSearch 中没有 currentPhoto，且 currentPhoto 存在，则添加它
              // 这样可以确保 select_url 的结果能够匹配到 currentPhoto
              if (
                currentPhoto &&
                !photosToSearch.some((photo) => photo.seq === currentPhoto.seq)
              ) {
                photosToSearch = [...photosToSearch, currentPhoto]
              }

              let allEditedPhotos: PhotoItem[] = []
              let allNoChangesPhotos: PhotoItem[] = []
              let allTimeoutPhotos: PhotoItem[] = []
              let allFailedPhotos: PhotoItem[] = []
              let allFailureReasons: string[] = []

              outputData.forEach((outputItem) => {
                const result = processOutputItem(
                  outputItem,
                  taskId,
                  photosToSearch
                )
                allEditedPhotos.push(...result.editedPhotos)
                allNoChangesPhotos.push(...result.noChangesPhotos)
                allTimeoutPhotos.push(...result.timeoutPhotos)
                allFailedPhotos.push(...result.failedPhotos)
                allFailureReasons.push(...result.failureReasons)
              })

              // 更新照片状态
              updatePhotosStatusByCategory(
                allEditedPhotos,
                allNoChangesPhotos,
                allTimeoutPhotos,
                allFailedPhotos
              )

              // 设置结果弹窗
              setResultModalData(
                allEditedPhotos,
                allNoChangesPhotos,
                allTimeoutPhotos,
                allFailedPhotos,
                allFailureReasons
              )

              setLoading(false)
            } else {
              // 没有有效的输出数据，根据本次处理的照片状态分类
              const photosToCheck =
                currentProcessingPhotos.length > 0
                  ? currentProcessingPhotos
                  : processingPhotoList
              const categorized = categorizePhotosByStatus(photosToCheck)
              setResultModalData(
                categorized.editedPhotos,
                categorized.noChangesPhotos,
                categorized.timeoutPhotos,
                categorized.failedPhotos,
                []
              )
              setLoading(false)
            }
          } catch (error) {
            // output格式错误时，按照超时处理
            console.error('解析output数据失败:', error)
            const photosToTimeout =
              currentProcessingPhotos.length > 0
                ? currentProcessingPhotos
                : processingPhotoList

            const timeoutPhotosWithError = photosToTimeout.map((photo) => ({
              ...photo,
              watermarkStatus: WatermarkExtractionStatus.EDIT_TIMEOUT,
              errorMsg: '编辑超时',
              reason: 'output格式错误'
            }))

            if (timeoutPhotosWithError.length > 0) {
              updatePhotosStatus(
                timeoutPhotosWithError,
                WatermarkExtractionStatus.EDIT_TIMEOUT
              )
            }

            setResultModal({
              visible: true,
              successCount: 0,
              successPhotos: [],
              noChangesCount: 0,
              noChangesPhotos: [],
              timeoutCount: photosToTimeout.length,
              timeoutPhotos: timeoutPhotosWithError,
              failedCount: 0,
              failedPhotos: [],
              failureReason: ''
            })

            // 埋点上报：output格式错误，按超时处理，算作失败
            trackWatermarkBatchEditor({
              actionType: 'showProcessCompletePop',
              number5: 0,
              number6: photosToTimeout.length,
              photoNum: photoList.length
            })

            setLoading(false)
          }
        } else {
          // 没有output数据，根据本次处理的照片状态分类
          const photosToCheck =
            currentProcessingPhotos.length > 0
              ? currentProcessingPhotos
              : processingPhotoList
          const categorized = categorizePhotosByStatus(photosToCheck)
          setResultModalData(
            categorized.editedPhotos,
            categorized.noChangesPhotos,
            categorized.timeoutPhotos,
            categorized.failedPhotos,
            []
          )
          setLoading(false)
        }
      } else if (pollResult.data?.taskStatus === 4) {
        // 状态 4：处理失败
        const failureMsg = pollResult.data?.msg || '任务处理失败，请稍后重试'
        message.error(failureMsg)

        const photosToFail =
          currentProcessingPhotos.length > 0
            ? currentProcessingPhotos
            : processingPhotoList

        handleFailure(photosToFail, failureMsg, currentPhoto)
        setLoading(false)
      } else if (pollResult.data?.taskStatus === 5) {
        // 状态 5：超时完成
        const timeoutMsg = pollResult.data?.msg || '任务处理超时，请稍后重试'
        message.warning(timeoutMsg)

        const photosToTimeout =
          currentProcessingPhotos.length > 0
            ? currentProcessingPhotos
            : processingPhotoList

        const timeoutPhotosWithError = photosToTimeout.map((photo) => ({
          ...photo,
          watermarkStatus: WatermarkExtractionStatus.EDIT_TIMEOUT,
          errorMsg: timeoutMsg,
          reason: timeoutMsg
        }))

        if (timeoutPhotosWithError.length > 0) {
          updatePhotosStatus(
            timeoutPhotosWithError,
            WatermarkExtractionStatus.EDIT_TIMEOUT
          )
        }

        setResultModal({
          visible: true,
          successCount: 0,
          successPhotos: [],
          noChangesCount: 0,
          noChangesPhotos: [],
          timeoutCount: photosToTimeout.length,
          timeoutPhotos: timeoutPhotosWithError,
          failedCount: 0,
          failedPhotos: [],
          failureReason: ''
        })

        // 埋点上报：任务超时，算作失败
        trackWatermarkBatchEditor({
          actionType: 'showProcessCompletePop',
          number5: 0,
          number6: photosToTimeout.length,
          photoNum: photoList.length
        })

        setLoading(false)
      } else {
        // 其他未知状态，按失败处理
        const failureMsg =
          pollResult.data?.msg ||
          `任务状态异常（状态码：${pollResult.data?.taskStatus}），请稍后重试`
        message.error(failureMsg)

        const photosToFail =
          currentProcessingPhotos.length > 0
            ? currentProcessingPhotos
            : processingPhotoList

        handleFailure(photosToFail, failureMsg, currentPhoto)
        setLoading(false)
      }
    },
    [
      processOutputItem,
      photoList,
      updatePhotosStatusByCategory,
      setResultModalData,
      categorizePhotosByStatus,
      updatePhotosStatus,
      trackWatermarkBatchEditor,
      handleFailure,
      currentPhoto
    ]
  )

  /**
   * 处理创建AI任务的结果
   */
  const handleCreateAITask = useCallback(
    (
      taskParams: any,
      photoCount: number,
      currentProcessingPhotos: PhotoItem[],
      processingPhotoList: PhotoItem[],
      successMessage: string
    ) => {
      createAITask(taskParams)
        .then((result) => {
          if (
            result &&
            result.code === 200 &&
            result.data &&
            result.data.status === 0
          ) {
            const taskId = result.data.taskID || ''

            // 轮询任务结果
            pollAITaskResult(
              { taskID: taskId, userID: userId || '' },
              { photoCount }
            )
              .then((pollResult) => {
                handlePollResult(
                  pollResult,
                  taskId,
                  currentProcessingPhotos,
                  processingPhotoList,
                  successMessage
                )
              })
              .catch((error) => {
                console.error('轮询任务状态失败:', error)
                const errorMsg =
                  error?.message ||
                  error?.msg ||
                  '任务处理失败或超时，请稍后重试'
                message.error(errorMsg)

                const photosToFail = getPhotosToFail(
                  currentProcessingPhotos,
                  processingPhotoList
                )
                handleFailure(photosToFail, errorMsg, currentPhoto)
                setLoading(false)
              })
          } else {
            // 创建任务失败
            const errorMsg = result?.msg || '水印应用失败！'
            message.error(errorMsg)

            const photosToFail = getPhotosToFail(
              currentProcessingPhotos,
              processingPhotoList
            )
            handleFailure(photosToFail, errorMsg, currentPhoto)
            setLoading(false)
          }
        })
        .catch((error) => {
          console.error('创建AI任务失败:', error)
          message.error('水印应用失败！')

          const photosToFail = getPhotosToFail(
            currentProcessingPhotos,
            processingPhotoList
          )
          handleFailure(photosToFail, '创建任务失败，请重试', currentPhoto)
          setLoading(false)
        })
    },
    [
      createAITask,
      userId,
      pollAITaskResult,
      handlePollResult,
      getPhotosToFail,
      handleFailure,
      currentPhoto
    ]
  )

  // 处理水印应用 - 统一函数
  const handleApplyWatermark = useCallback(
    (
      applyType: 'current' | 'all' | 'selected',
      editedItems: WatermarkItem[],
      selectedPhotos?: PhotoItem[]
    ): void => {
      setLoading(true)

      // 1. 构建请求参数
      const applyParams = buildApplyParams(applyType, selectedPhotos)
      if (!applyParams) {
        setLoading(false)
        return
      }

      const {
        urlList,
        photoCount,
        processingPhotos,
        successMessage
      } = applyParams

      // 保存正在处理的照片列表，用于后续显示结果（特别是超时情况）
      setProcessingPhotoList(processingPhotos)
      const currentProcessingPhotos = processingPhotos // 保存为闭包变量
      setProcessingPhotoCount(photoCount)

      // 2. 构建水印列表
      const { srcList, editList } = buildWatermarkLists(
        editedItems,
        currentPhoto
      )

      // 3. 构建AI任务请求参数
      const taskParams = {
        aiTaskType: 14,
        userID: userId || '',
        input: {
          urls: urlList,
          select_url: currentPhoto?.aiURL || currentPhoto?.smallURL,
          src_list: srcList,
          edit_list: editList,
          edit_user: userId || ''
        }
      }

      // 4. 调用AI任务接口并处理结果
      handleCreateAITask(
        taskParams,
        photoCount,
        currentProcessingPhotos,
        processingPhotoList,
        successMessage
      )
    },
    [
      buildApplyParams,
      buildWatermarkLists,
      userId,
      handleCreateAITask,
      currentPhoto,
      processingPhotoList
    ]
  )

  const closeResultModal = useCallback(() => {
    setResultModal({
      visible: false,
      successCount: 0,
      successPhotos: [],
      noChangesCount: 0,
      noChangesPhotos: [],
      timeoutCount: 0,
      timeoutPhotos: [],
      failedCount: 0,
      failedPhotos: [],
      failureReason: ''
    })
    // 确保 loading 状态也被重置
    setLoading(false)
  }, [])

  return {
    loading,
    handleApplyWatermark,
    resultModal,
    closeResultModal,
    processingPhotoCount
  }
}

export default useWatermarkApply
